import './edit-retailerGM.scss';
import Header from '../../../organisms/header/header.js';
import { Link, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import decodeToken from '../../../../utils/decode-token';
import { Alert, Snackbar } from '@mui/material';
import Grid from "@mui/material/Grid2";
import Input from "../../../atoms/input";
import apiClient from "../../../../utils/apiClient";
import { useForm } from 'react-hook-form';
import Joi from "joi";
import { joiResolver } from "@hookform/resolvers/joi";
import { PhoneInput } from 'react-international-phone';
import 'react-international-phone/style.css';
import {parsePhoneNumberFromString} from "libphonenumber-js";

function EditRetailerGM() {
  const { t } = useTranslation();
  const token = sessionStorage.getItem('token') || null;
  const role = decodeToken(token).role;
  const { retailerId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const retailerGMEmail = location.state?.retailerGMEmail || ''; // Email pasado desde el estado

  const [retailerGM, setRetailerGM] = useState({});
  const [areaCode, setAreaCode] = useState(location.state?.areaCode || '');
  const [retailerName, setRetailerName] = useState('');
  const [phone, setPhone] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [languages, setLanguages] = useState([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('');

  const { breadcrumbName } = location.state || {};
  //console.log(breadcrumbName);

  // Obtener datos del Retailer GM por correo
  useEffect(() => {
    const fetchRetailerGM = async () => {

      try {
        const url = `${process.env.REACT_APP_API_URL}/retailer/staff-member?staffEmail=${encodeURIComponent(retailerGMEmail)}`;
        const config = {
          headers: {
            Authorization: `Bearer ${token}`,
            'Cache-Control': 'no-cache',
          },
        };
        const response = await apiClient.get(url, config);
        if (response.status === 200) {
          setRetailerGM(response.data); // Almacenar la respuesta en retailerGM
          setPhone(`${response.data.prefix_phone}${response.data.phone}`);
        }
      } catch (error) {
        setSnackbarOpen(true);
        setSnackbarSeverity('error');
        setSnackbarMessage(t('common-internal-server-error'));
      }
    };

    if (retailerGMEmail) {
      fetchRetailerGM();
    }
  }, [retailerGMEmail, token, t]);

  // Llamada para obtener el área_code del retailer
  useEffect(() => {
    const fetchRetailerData = async () => {
      const url = `${process.env.REACT_APP_API_URL}/retailer?retailerId=${retailerId}`;
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
          'Cache-Control': 'no-cache',
        },
      };

      try {
        const response = await apiClient.get(url, config);
        if (response.status === 200 && response.data.retailer.area_code) {
          setAreaCode(response.data.retailer.area_code);
          setRetailerName(response.data.retailer.name);
        }
      } catch (error) {
        setSnackbarOpen(true);
        setSnackbarSeverity('error');
        setSnackbarMessage(t('common-internal-server-error'));
      }
    };

    fetchRetailerData();
  }, [retailerId, token, t]);


  const schema =  Joi.object({
    name: Joi.string().required().messages({ 'string.empty': t('alert-name') }),
    last_name: Joi.string().required().messages({ 'string.empty': t('alert-last-name') }),
    email: Joi.string().trim().email({ tlds: { allow: false } }).required().messages({
      'string.email': t('alert-email-format'),
      'string.empty': t('alert-email'),
    }),
    prefix_phone: Joi.string().pattern(/^\+[0-9]{2,3}$/).required().messages({
      'string.pattern.base': t('alert-prefix'),
      'string.empty': t('alert-prefix'),
    }),
    phone: Joi.string().pattern(/^[0-9]{7,15}$/).required().messages({
      'string.pattern.base': t('alert-phone-format'),
      'string.empty': t('alert-phone'),
    }),
    preferred_language: Joi.string().required().messages({ 'string.empty': t('alert-language') }),
  })

  // Uso de react-hook-form y Joi para manejar y validar el formulario
  const { register, handleSubmit, formState, setValue, setFocus} = useForm({
    resolver: joiResolver(schema),
    mode: 'onChange'
  });
  const { errors } = formState;

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  // Cargar datos iniciales
  useEffect(() => {
    if (retailerGM) {
      setValue('name', retailerGM.name || '');
      setValue('email', retailerGM.email || '');
      setValue('last_name', retailerGM.last_name || '');
      setValue('prefix_phone', retailerGM.prefix_phone || '');
      setValue('phone', retailerGM.phone || '');
      setValue('preferred_language', retailerGM.preferred_language || '');

      const fetchLanguages = async () => {
        try {
          const response = await apiClient.get(`${process.env.REACT_APP_API_URL}/hac/countries`);
          const country = response.data.find((country) => country.area_code === areaCode);
          if (country && country.language) {
            const formattedLanguages = country.language.map((lang) => ({
              code: lang,
              name: t(`languages-${lang}`), // Traducir el idioma
            }));
            // Asegúrate de que el idioma preferido esté en las opciones
            if (
              retailerGM.preferred_language &&
              !formattedLanguages.some((lang) => lang.code === retailerGM.preferred_language)
            ) {
              formattedLanguages.unshift({
                code: retailerGM.preferred_language,
                name: t(`languages-${retailerGM.preferred_language}`),
              });
            }
            setLanguages(formattedLanguages);
          } else {
            setLanguages([]); // Asegúrate de manejar casos donde no haya idiomas
          }
        } catch (error) {
          setSnackbarSeverity('error');
          setSnackbarOpen(true);
          setSnackbarMessage(t('common-internal-server-error'));
        }
      };
      if (areaCode) fetchLanguages();
    }
  }, [retailerGM, areaCode, setValue, t]);



  const onSubmit = async (data) => {

    let payload = {
      name: data.name,
      last_name: data.last_name,
      prefix_phone: data.prefix_phone,
      phone: data.phone,
      language: data.preferred_language
    };

    const parsedPhone = parsePhoneNumberFromString(phone);

    if (!parsedPhone || !parsedPhone.isValid()) {
      setPhoneError(t('alert-phone-format'));
      setFocus('phone');
      return;
    }

    else {
      setPhoneError('');

      payload.prefix_phone = `+${parsedPhone.countryCallingCode}`;
      payload.phone = parsedPhone.nationalNumber;
    }

    try {
      const response = await apiClient.patch(
        `${process.env.REACT_APP_API_URL}/retailer/retailer-staff/personal-settings?staffId=${retailerGM.id}`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.status === 200 || response.status === 204) {
        setSnackbarMessage(t('alert-data-saved'));
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        setTimeout(() => navigate(-1), 2000); // Regresa a la página anterior
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      setSnackbarMessage(t('common-internal-server-error'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };


  return (
    <>
      {token && (role === 'admin' || role === 'marketAdmin' || role === 'countryAdmin') ? (
        <div className="App">
          <Header />
          <main>
            <section className="form">
              <div className="inner">
                <form onSubmit={handleSubmit(onSubmit)}>
                  <div className="back-button">
                    <Link
                      to="#"
                      onClick={(e) => {
                        e.preventDefault();
                        navigate(-1);
                      }}
                      className="back-button"
                    >
                      <span className="icon-chevron-left"></span>{' '}{t('common-back')}
                    </Link>
                    {' '} / {retailerName} {' '} / {breadcrumbName}
                  </div>
                  <h2>{t('admin-edit-retailerGM-title')}</h2>

                  <Grid container spacing={3}>
                    <Grid size={{ xs: 12, sm: 6, md: 5 }}>
                      <div className="form-control required">
                        <Input
                          maxLength="50"
                          type="text"
                          id="name"
                          label={t('common-name')}
                          {...register('name')}
                          error={errors?.name}
                        />
                      </div>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 5 }}>
                      <div className="form-control required">
                        <Input
                          maxLength="50"
                          type="text"
                          id="last_name"
                          label={t('common-last-name')}
                          {...register('last_name')}
                          error={errors?.last_name}
                        />
                      </div>
                    </Grid>
                  </Grid>

                  <Grid container spacing={3}>
                    <Grid size={{ xs: 12, sm: 12, md: 6 }}>
                      <div className="form-control required">
                        <Input
                          maxLength="50"
                          type="text"
                          id="email"
                          readOnly
                          label={t('common-email')}
                          {...register('email')}
                          disabled // El correo no debe ser editable
                        />
                      </div>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 12, md: 5 }}>
                      <div className="form-control required">
                        <label htmlFor="phone">{t('common-phone')}</label>
                        <PhoneInput
                          value={phone}
                          onChange={(value) => {
                            setPhone(value);
                            setPhoneError('');
                          }}
                          onBlur={() => {
                            const parsed = parsePhoneNumberFromString(phone);
                            if (!parsed || !parsed.isValid()) {
                              setPhoneError(t('alert-phone-format'));
                            } else {
                              setPhoneError('');
                            }
                          }}
                        />
                        {phoneError && <p className="error-message">{phoneError}</p>}
                      </div>
                    </Grid>
                  </Grid>

                  <Grid container spacing={3}>
                    <Grid size={{ xs: 12, sm: 1, md: 6 }}>
                      <div className="form-control required">
                        <label htmlFor="preferred_language">{t('common-select-language')}</label>
                        <select
                          name="preferred_language"
                          id="preferred_language"
                          className="normal"
                          value={retailerGM?.preferred_language || ''}
                          disabled={(role === 'admin' || role === 'marketAdmin' || role === 'countryAdmin')}
                          {...register('preferred_language')}
                        >
                          <option value="">{t('common-select-language')}</option>
                          {languages.map(({code, name}) => (
                            <option key={code} value={code}>
                              {name}
                            </option>
                          ))}
                        </select>
                        {errors.preferred_language && (
                          <span className="input__error">{errors.preferred_language.message}</span>
                        )}
                      </div>
                    </Grid>
                  </Grid>

                  <Grid container spacing={3}>
                    <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                      <button className="btn">
                        {t('common-save')}
                      </button>
                    </Grid>
                  </Grid>
                </form>

              </div>
            </section>
          </main>
          <Snackbar open={snackbarOpen} autoHideDuration={3000} onClose={handleSnackbarClose}>
            <Alert severity={snackbarSeverity} sx={{width: '100%'}}>
              {snackbarMessage}
            </Alert>
          </Snackbar>
        </div>
      ) : (
        <Navigate to="/login"/>
      )}
    </>
  );
}

export default EditRetailerGM;
